import {useUserStore} from '~/stores/user'
import {portalHeaders} from '~/config/portal'
import type {ApiResponse} from '~/utils/types'

/**
 * BI P3 站內答題四支端點（`docs/behavior-insights/implementation-p3.md` §2）。
 * 對應 reservation-server `internal/rest/feedback.go`：`OptionalJWT`，登入租客走
 * Bearer token，匿名／case-only 走 `?token=` query（見 `resolveAuth`，兩種方式全部
 * 端點皆適用，含 POST——後端讀的是 `c.Query("token")`，不分 method）。
 *
 * 刻意不走 `composables/requset.ts` 的 `httpRequest()`：這四支端點回應確實是正常
 * envelope（不是 analytics.ts 那種裸 204，那個例外不適用這裡），但 `httpRequest()`
 * 在 `error_code !== '00000'` 時會彈全域 `ElMessage` 錯誤 toast——回饋是加分項不是
 * 必要流程（§1.2 第 5 點、§5），任何失敗都必須安靜吞掉，不能因為一次答題失敗就在
 * 使用者眼前跳錯誤訊息。這與 vito 端不用 `callAPI(..., silent=true)` 是同一個理由
 * （implementation-p3.md §3）。
 */

const FEEDBACK_NEXT_PATH = '/api/feedback/next'
const FEEDBACK_ANSWER_PATH = '/api/feedback/answer'
const FEEDBACK_DECLINE_PATH = '/api/feedback/decline'
const FEEDBACK_SELF_REPORT_PATH = '/api/feedback/self-report'

export interface FeedbackNextResult {
    request_ref: number | null
    campaign: string
    question_version: number
    subject_type: string
    expires_at: string | null
}

export interface FeedbackAnswerItem {
    question_key: string
    answer_code: string
    optional_text?: string
}

export interface FeedbackSelfReportResult {
    request_id: number
    created: boolean
    token?: string
}

interface FeedbackApiOutcome<T> {
    success: boolean
    data: T | null
}

function withToken(path: string, token?: string): string {
    return token ? `${path}?token=${encodeURIComponent(token)}` : path
}

async function callFeedbackApi<T>(method: 'GET' | 'POST', url: string, body?: Record<string, unknown>): Promise<FeedbackApiOutcome<T>> {
    try {
        const config = useRuntimeConfig()
        const userStore = useUserStore()
        const headers: Record<string, string> = {...portalHeaders()}
        if (userStore.getToken) {
            headers['Authorization'] = 'Bearer ' + userStore.getToken
        }

        const res: ApiResponse = await $fetch(config.public.baseURL + url, {
            method,
            credentials: 'include',
            headers,
            ...(body ? {body} : {}),
        })

        if (res.error_code === '00000') {
            return {success: true, data: (res.data ?? null) as T | null}
        }
        return {success: false, data: null}
    } catch (error) {
        console.warn(`[feedback] ${url} 失敗:`, error)
        return {success: false, data: null}
    }
}

// GET /api/feedback/next —— 有待答項目時回內容，沒有則回 null（不是拋錯）。
export async function getNextFeedback(token?: string): Promise<FeedbackNextResult | null> {
    const {data} = await callFeedbackApi<FeedbackNextResult>('GET', withToken(FEEDBACK_NEXT_PATH, token))
    return data
}

// POST /api/feedback/answer —— requestId 只在登入路徑需要，token 路徑由 token 本身解析該筆。
export async function postFeedbackAnswer(requestId: number, answers: FeedbackAnswerItem[], token?: string): Promise<boolean> {
    const {success} = await callFeedbackApi('POST', withToken(FEEDBACK_ANSWER_PATH, token), {request_id: requestId, answers})
    return success
}

// POST /api/feedback/decline —— 明確拒答（與「稍後」不同，稍後不呼叫任何 API）。
export async function postFeedbackDecline(requestId: number, token?: string): Promise<boolean> {
    const {success} = await callFeedbackApi('POST', withToken(FEEDBACK_DECLINE_PATH, token), {request_id: requestId})
    return success
}

// POST /api/feedback/self-report —— 使用者主動回報，跳過 Next()，不受頻控。
export async function postFeedbackSelfReport(
    subjectType: string,
    subjectId: number,
    answers: FeedbackAnswerItem[],
    token?: string
): Promise<FeedbackSelfReportResult | null> {
    const {data} = await callFeedbackApi<FeedbackSelfReportResult>(
        'POST',
        withToken(FEEDBACK_SELF_REPORT_PATH, token),
        {subject_type: subjectType, subject_id: subjectId, answers}
    )
    return data
}
